import { useEffect, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonCard,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonModal,
  IonPicker,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonSpinner,
  IonTitle,
  IonToast,
  IonToolbar,
} from "@ionic/react";
import { getFarmIdsOfUser, getPlotIdsOfFarm } from "../services/farm.service";
import { getCategories, getSupportAgents } from "../services/ticket.service";
import UserList from "../pages/UserList";

const FarmPicker = { name: "FarmId", options: [] as any[] };
const setFarmPicker = (farmIds: any[]) => {
  FarmPicker.options = farmIds.map(item => ({ text: item.farmId, value: item.farmId }));
};

const PlotPicker = { name: "PlotId", options: [] as any[] };
const setPlotPicker = (plotIds: any[]) => {
  PlotPicker.options = plotIds.map(item => ({ text: item.plotId, value: item.plotId }));
};

interface IProps {
  formik?: any;
  isUpdate?: boolean;
}

const NewTicket: React.FC<IProps> = (props) => {
  const { formik, isUpdate = false } = props;

  const [showSelectUserModal, setShowSelectUserModal] = useState(false);
  const [showAgentUserModal, setShowAgentUserModal] = useState(false);
  const [onError, setOnError] = useState("");
  const [categories, setCategories] = useState<string[]>([]);
  const [allAgents, setAllAgents] = useState<any[]>([]);
  const [agentsList, setAgentsList] = useState<any[]>([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [farmPickerIsOpen, setFarmPickerIsOpen] = useState(false);
  const [plotPickerIsOpen, setPlotPickerIsOpen] = useState(false);

  useEffect(() => {
    getCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (!showAgentUserModal) { setSearchTerm(""); return; }
    // Load all support agents once when modal opens (role-based, not userTypeId)
    setAgentsLoading(true);
    getSupportAgents()
      .then(agents => { setAllAgents(agents); setAgentsList(agents); })
      .catch(() => { setAllAgents([]); setAgentsList([]); })
      .finally(() => setAgentsLoading(false));
  }, [showAgentUserModal]);

  const handleAgentSearch = (term: string) => {
    setSearchTerm(term);
    if (!term.trim()) { setAgentsList(allAgents); return; }
    const q = term.trim().toLowerCase();
    setAgentsList(allAgents.filter(a =>
      a.name?.toLowerCase().includes(q) ||
      a.farmUserId?.toLowerCase().includes(q) ||
      a.mobile?.includes(q)
    ));
  };

  function setCustomerUserId(item: any) {
    formik.setFieldValue("customerUserId", item.farmUserId);
    formik.setFieldValue("customerMobile", item.mobile);
    formik.setFieldValue("farmId", "");
    formik.setFieldValue("plotId", "");
    formik.setFieldValue("category", "");
    formik.setFieldValue("description", "");
    setShowSelectUserModal(false);
  }

  const compareWith = (o1: any, o2: any) => {
    if (Array.isArray(o2)) return o2.indexOf(o1) !== -1;
    return o1 === o2;
  };

  useEffect(() => {
    let mounted = true;
    if (formik.values.customerUserId) {
      getFarmIdsOfUser(formik.values.customerUserId).then((items) => {
        if (mounted) setFarmPicker(items);
      });
    }
    return () => { mounted = false; };
  }, [formik.values.customerUserId]);

  useEffect(() => {
    let mounted = true;
    if (formik.values.farmId) {
      getPlotIdsOfFarm(formik.values.farmId).then((items) => {
        if (mounted) setPlotPicker(items);
      });
    }
    return () => { mounted = false; };
  }, [formik.values.farmId]);

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <IonList>
          {!isUpdate && (
            <>
              <IonItem>
                <IonLabel position="stacked">Customer</IonLabel>
                <IonInput
                  readonly
                  value={formik.values.customerUserId}
                  placeholder="Select Customer"
                  onClick={() => setShowSelectUserModal(true)}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Farm Id</IonLabel>
                <IonInput
                  readonly
                  disabled={!formik.values.customerUserId}
                  value={formik.values.farmId}
                  placeholder="Select Farm Id"
                  onClick={() => setFarmPickerIsOpen(true)}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Plot Id</IonLabel>
                <IonInput
                  readonly
                  disabled={!formik.values.farmId}
                  value={formik.values.plotId}
                  placeholder="Select Plot Id"
                  onClick={() => setPlotPickerIsOpen(true)}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Category</IonLabel>
                <IonSelect
                  compareWith={compareWith}
                  value={formik.values.category}
                  placeholder="Select Category"
                  onIonChange={(e) => formik.setFieldValue("category", e.detail.value)}
                >
                  {categories.map(item => (
                    <IonSelectOption key={item} value={item}>{item}</IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              {formik.touched.category && formik.errors.category ? (
                <div className="errorMsg">{formik.errors.category}</div>
              ) : null}

              <IonItem>
                <IonLabel position="stacked">Description</IonLabel>
                <IonTextarea
                  value={formik.values.description}
                  placeholder="Describe the issue..."
                  onIonChange={(e) => formik.setFieldValue("description", e.detail.value)}
                />
              </IonItem>
            </>
          )}

          {isUpdate && (
            <IonItem>
              <IonLabel position="stacked">Category</IonLabel>
              <IonInput readonly value={formik.values.category} />
            </IonItem>
          )}

          <IonItem>
            <IonLabel position="stacked">Assign To (optional)</IonLabel>
            <IonInput
              readonly
              value={formik.values.assigneeName}
              placeholder="Select Agent"
              onClick={() => setShowAgentUserModal(true)}
            />
          </IonItem>

          <IonItem>
            <IonLabel position="stacked">Priority</IonLabel>
            <IonSelect
              compareWith={compareWith}
              value={formik.values.priority}
              placeholder="Priority"
              onIonChange={(e) => formik.setFieldValue("priority", e.detail.value)}
            >
              <IonSelectOption value="low">Low</IonSelectOption>
              <IonSelectOption value="normal">Normal</IonSelectOption>
              <IonSelectOption value="high">High</IonSelectOption>
            </IonSelect>
          </IonItem>
        </IonList>
      </form>

      {showSelectUserModal && (
        <IonModal isOpen={showSelectUserModal}>
          <UserList
            selectedValue={formik.values.customerUserId}
            onClickHandler={setCustomerUserId}
            onClose={setShowSelectUserModal}
          />
        </IonModal>
      )}

      {showAgentUserModal && (
        <IonModal isOpen={showAgentUserModal}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowAgentUserModal(false)}>Close</IonButton>
              </IonButtons>
              <IonTitle>Agents</IonTitle>
            </IonToolbar>
            <IonToolbar>
              <IonSearchbar
                placeholder="Search by name..."
                value={searchTerm}
                debounce={400}
                onIonChange={(e) => handleAgentSearch(e.detail.value ?? '')}
              />
            </IonToolbar>
          </IonHeader>
          <IonContent>
            {agentsLoading
              ? <IonSpinner style={{ display: 'block', margin: '20px auto' }} />
              : agentsList.map((agent: any) => (
                <IonCard
                  key={agent.farmUserId}
                  onClick={() => {
                    formik.setFieldValue("assigneeId", agent.farmUserId);
                    formik.setFieldValue("assigneeName", agent.name);
                    setShowAgentUserModal(false);
                  }}
                >
                  <IonItem lines="none">
                    <IonLabel>
                      <h2>{agent.name}</h2>
                      <p>{agent.farmUserId}{agent.mobile ? ` · ${agent.mobile}` : ''}</p>
                    </IonLabel>
                  </IonItem>
                </IonCard>
              ))}
          </IonContent>
        </IonModal>
      )}

      <IonPicker
        isOpen={farmPickerIsOpen}
        columns={[FarmPicker]}
        buttons={[
          { text: "Cancel", role: "cancel", handler: () => setFarmPickerIsOpen(false) },
          {
            text: "Confirm",
            handler: (value) => {
              formik.values.farmId = value.FarmId.value;
              formik.values.plotId = "";
              setFarmPickerIsOpen(false);
            },
          },
        ]}
      />

      <IonPicker
        isOpen={plotPickerIsOpen}
        columns={[PlotPicker]}
        buttons={[
          { text: "Cancel", role: "cancel", handler: () => setPlotPickerIsOpen(false) },
          {
            text: "Confirm",
            handler: (value) => {
              formik.values.plotId = value.PlotId.value;
              setPlotPickerIsOpen(false);
            },
          },
        ]}
      />

      <IonToast
        isOpen={!!onError}
        onDidDismiss={() => setOnError("")}
        message={onError}
        duration={2000}
        color="danger"
      />
    </>
  );
};

export default NewTicket;
